import { FC, PropsWithChildren, useRef, useEffect } from 'react'

import getScrollParent from './getScrollParent'

const FootnoteLinks: FC<PropsWithChildren> = ({ children }) => {
  const containerRef = useRef<HTMLDivElement>(null)

  const onClickCapture = (e: MouseEvent) => {
    const target = e.target as HTMLElement | null
    const link = target?.closest('a') as HTMLAnchorElement | null

    const href = link?.getAttribute('href') || ''
    if (!href || !href.startsWith('#')) return

    e.preventDefault()
    e.stopPropagation()

    const id = decodeURIComponent(href.slice(1)) // exept hashtag

    const destination = document.getElementById(id)

    if (!destination) return

    const rect = destination.getBoundingClientRect()
    const padding = 16
    const scrollParent = getScrollParent(destination)

    if (scrollParent) {
      const parentRect = scrollParent.getBoundingClientRect()
      const isInViewport =
        rect.top >= parentRect.top + padding &&
        rect.bottom <= parentRect.bottom - padding

      if (isInViewport) return

      const destinationCenterWithinParent =
        rect.top - parentRect.top + scrollParent.scrollTop + rect.height / 2

      const desiredScrollTop =
        destinationCenterWithinParent - scrollParent.clientHeight / 2

      const top = Math.min(
        Math.max(0, desiredScrollTop),
        scrollParent.scrollHeight - scrollParent.clientHeight,
      )

      scrollParent.scrollTo({ top, behavior: 'smooth' })
    } else {
      destination.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  }

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container.addEventListener('click', onClickCapture)
    return () => {
      container.removeEventListener('click', onClickCapture)
    }
  }, [])

  return <div ref={containerRef}>{children}</div>
}

export default FootnoteLinks
